import React, { useRef, useState } from "react";
import { notify } from "../Notification";
import { formatListTime } from "../../hooks/useClock";
import { CONTACTS, MY_CARD, RECENTS, fullName, sections } from "../../data/contacts";
import Avatar from "./Avatar";
import { AppShell, BackButton, Decor, Screen, SearchField, useNavStack } from "./ios";
import { ReactComponent as ChevronLeft } from "../../assets/svgs/sf-symbols/ios/chevron.left.svg";
import { ReactComponent as StarIcon } from "../../assets/svgs/sf-symbols/phone/star.fill.svg";
import { ReactComponent as ClockIcon } from "../../assets/svgs/sf-symbols/phone/clock.fill.svg";
import { ReactComponent as PersonCircleIcon } from "../../assets/svgs/sf-symbols/phone/person.crop.circle.fill.svg";
import { ReactComponent as KeypadIcon } from "../../assets/svgs/sf-symbols/phone/circle.grid.3x3.fill.svg";
import { ReactComponent as VoicemailIcon } from "../../assets/svgs/sf-symbols/phone/recordingtape.svg";
import { ReactComponent as InfoIcon } from "../../assets/svgs/sf-symbols/phone/info.circle.svg";
import { ReactComponent as FilterIcon } from "../../assets/svgs/sf-symbols/phone/line.3.horizontal.decrease.svg";
import { ReactComponent as PlusIcon } from "../../assets/svgs/sf-symbols/phone/plus.svg";
import { ReactComponent as PhoneIcon } from "../../assets/svgs/sf-symbols/phone/phone.fill.svg";
import { ReactComponent as MessageIcon } from "../../assets/svgs/sf-symbols/phone/message.fill.svg";
import { ReactComponent as VideoIcon } from "../../assets/svgs/sf-symbols/phone/video.fill.svg";
import { ReactComponent as MailIcon } from "../../assets/svgs/sf-symbols/phone/envelope.fill.svg";
import { ReactComponent as OutIcon } from "../../assets/svgs/sf-symbols/phone/arrow.up.right.svg";
import { ReactComponent as InIcon } from "../../assets/svgs/sf-symbols/phone/arrow.down.left.svg";
import { ReactComponent as DeleteIcon } from "../../assets/svgs/sf-symbols/phone/delete.left.svg";

const byId = Object.fromEntries(CONTACTS.map((c) => [c.id, c]));
const ALPHABET = [..."ABCDEFGHIJKLMNOPQRSTUVWXYZ", "#"];
const KEYS = [
  ["1", ""], ["2", "ABC"], ["3", "DEF"],
  ["4", "GHI"], ["5", "JKL"], ["6", "MNO"],
  ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
  ["*", ""], ["0", "+"], ["#", ""],
];
const TABS = [
  ["favourites", "Favourites", StarIcon],
  ["recents", "Recents", ClockIcon],
  ["contacts", "Contacts", PersonCircleIcon],
  ["keypad", "Keypad", KeypadIcon],
  ["voicemail", "Voicemail", VoicemailIcon],
];

const noCalls = () =>
  notify("Calls and messages don't go out from this replica. Email me instead: it's on My Card in Contacts.");
const openLink = (href) => window.open(href, "_blank", "noopener");

// A name as Contacts shows it: the part it's sorted by in bold.
const SortedName = ({ person }) =>
  person.last ? (
    <>
      {person.first} <b>{person.last}</b>
    </>
  ) : (
    <b>{person.first}</b>
  );

const Segmented = ({ value, onChange, options }) => (
  <div className="ph-seg glass glass--light" role="radiogroup">
    {options.map(([id, label]) => (
      <button
        type="button"
        key={id}
        role="radio"
        aria-checked={value === id}
        className={value === id ? "is-on" : ""}
        onClick={() => onChange(id)}
      >
        {label}
      </button>
    ))}
  </div>
);

// A row of a call list (Recents, Favourites): tapping it calls; the (i) opens the contact card.
const CallRow = ({ person, name, missed, sub, time, onInfo }) => (
  <div className="ph-row">
    <button type="button" className="ph-row-main" onClick={noCalls} aria-label={`Call ${name}`}>
      <Avatar person={person} />
      <span className="ph-row-text">
        <span className={`ph-row-name ${missed ? "is-missed" : ""}`}>{name}</span>
        <span className="ph-row-sub">{sub}</span>
      </span>
      {time && <span className="ph-row-time">{time}</span>}
    </button>
    <button type="button" className="ph-info" onClick={onInfo} aria-label={`About ${name}`}>
      <InfoIcon className="ph-sym-info" />
    </button>
  </div>
);

const RecentsScreen = ({ now, open }) => {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const calls = [...RECENTS].sort((a, b) => a.ago - b.ago).map((r) => {
    const person = r.who ? byId[r.who] : null;
    return { ...r, person, name: person ? fullName(person) : r.number, date: new Date(now.getTime() - r.ago * 60000) };
  })
    .filter((r) => filter === "all" || r.missed)
    .filter((r) => !q || r.name.toLowerCase().includes(q));

  return (
    <Screen
      title="Recents"
      left={<Decor className="ios-pill">Edit</Decor>}
      center={<Segmented value={filter} onChange={setFilter} options={[["all", "All"], ["missed", "Missed"]]} />}
      right={
        <Decor>
          <FilterIcon className="ph-sym-filter" />
        </Decor>
      }
    >
      <SearchField className="ph-search" value={query} onChange={setQuery} label="Search Recents" />
      {calls.map((r, i) => (
        <CallRow
          key={i}
          person={r.person}
          name={r.name}
          missed={r.missed}
          time={formatListTime(r.date, now)}
          sub={
            <>
              {!r.missed && (r.dir === "out" ? <OutIcon className="ph-sym-dir" /> : <InIcon className="ph-sym-dir" />)}
              {r.kind}
              {r.count ? ` (${r.count})` : ""}
            </>
          }
          onInfo={() => open(r.person || { number: r.number })}
        />
      ))}
      {!calls.length && <p className="ios-empty">{q ? "No Results" : "No Missed Calls"}</p>}
    </Screen>
  );
};

const FavouritesScreen = ({ open }) => (
  <Screen
    title="Favourites"
    left={<Decor className="ios-pill">Edit</Decor>}
    right={
      <Decor>
        <PlusIcon className="ph-sym-plus" />
      </Decor>
    }
  >
    {CONTACTS.filter((c) => c.favourite).map((c) => (
      <CallRow
        key={c.id}
        person={c}
        name={fullName(c)}
        sub={
          <>
            {c.favourite === "FaceTime" ? <VideoIcon className="ph-sym-kind" /> : <PhoneIcon className="ph-sym-kind" />}
            {c.favourite}
          </>
        }
        onInfo={() => open(c)}
      />
    ))}
  </Screen>
);

const ContactsScreen = ({ open }) => {
  const [query, setQuery] = useState("");
  const scroller = useRef(null);
  const q = query.trim().toLowerCase();
  const shown = sections(CONTACTS.filter((c) => !q || fullName(c).toLowerCase().includes(q)));

  // The A–Z index jumps to a letter's section, or the next one that has contacts.
  const jump = (letter) => {
    const target = shown.find((s) => s.letter >= letter && (letter !== "#" || s.letter === "#")) || shown[shown.length - 1];
    const el = target && scroller.current && scroller.current.querySelector(`[data-letter="${target.letter}"]`);
    if (el) el.scrollIntoView({ block: "start" });
  };

  return (
    <Screen
      title="Contacts"
      inlineTitle
      left={
        <Decor>
          <ChevronLeft className="ios-sym-back" />
        </Decor>
      }
      right={
        <Decor>
          <PlusIcon className="ph-sym-plus" />
        </Decor>
      }
      overlay={
        <nav className="ph-index" aria-label="Jump to letter">
          {ALPHABET.map((l) => (
            <button type="button" key={l} onClick={() => jump(l)}>
              {l}
            </button>
          ))}
        </nav>
      }
    >
      <div ref={scroller} className="ph-contacts">
        <SearchField className="ph-search" value={query} onChange={setQuery} label="Search Contacts" />
        {!q && (
          <button type="button" className="ph-mycard" onClick={() => open(MY_CARD)}>
            <Avatar person={MY_CARD} size={41} />
            <span className="ph-mycard-text">
              <b>{MY_CARD.first}</b>
              <small>My Card</small>
            </span>
          </button>
        )}
        {shown.map((s) => (
          <section key={s.letter} data-letter={s.letter} className="ph-section">
            <h2 className="ph-letter">{s.letter}</h2>
            {s.contacts.map((c) => (
              <button type="button" key={c.id} className="ph-contact" onClick={() => open(c)}>
                <Avatar person={c} size={41} />
                <span className="ph-contact-name">
                  <SortedName person={c} />
                </span>
              </button>
            ))}
          </section>
        ))}
        {!shown.length && <p className="ios-empty">No Results</p>}
      </div>
    </Screen>
  );
};

const KeypadScreen = () => {
  const [digits, setDigits] = useState("");
  return (
    <div className="ph-keypad">
      <p className="ph-dial" aria-live="polite">
        {digits}
      </p>
      <div className="ph-keys">
        {KEYS.map(([d, letters]) => (
          <button type="button" key={d} className="ph-key" onClick={() => setDigits((v) => (v + d).slice(0, 20))}>
            <span className="ph-key-digit">{d}</span>
            {letters && <span className="ph-key-letters">{letters}</span>}
          </button>
        ))}
        <span />
        <button type="button" className="ph-call" onClick={noCalls} aria-label="Call">
          <PhoneIcon className="ph-sym-call" />
        </button>
        {digits ? (
          <button type="button" className="ph-delete" onClick={() => setDigits((v) => v.slice(0, -1))} aria-label="Delete">
            <DeleteIcon className="ph-sym-delete" />
          </button>
        ) : (
          <span />
        )}
      </div>
    </div>
  );
};

const VoicemailScreen = () => (
  <Screen title="Voicemail">
    <p className="ios-empty">No Voicemail</p>
  </Screen>
);

// A contact card: picture, name, the message/call/video/mail buttons, then their details. My Card's
// details (email, website, LinkedIn) open; everyone else is made up, so their buttons just say so.
const CardScreen = ({ person, back }) => {
  const name = person.number || fullName(person);
  const fields = person.fields || [
    { label: person.number ? "phone" : "mobile", value: person.number || person.phone, call: true },
    ...(person.note ? [{ label: "notes", value: person.note }] : []),
  ];
  const email = person.fields && person.fields.find((f) => f.label === "email");
  const actions = [
    ["message", MessageIcon, noCalls],
    ["call", PhoneIcon, noCalls],
    ["video", VideoIcon, noCalls],
    ["mail", MailIcon, email ? () => openLink(email.href) : noCalls],
  ];

  return (
    <Screen left={<BackButton onClick={back} />} right={<Decor className="ios-pill">Edit</Decor>}>
      <div className="ph-card">
        <Avatar person={person.number ? null : person} size={104} />
        <h1 className="ph-card-name">{name}</h1>
        <div className="ph-card-actions">
          {actions.map(([label, Icon, run]) => (
            <button type="button" key={label} className="ph-action" onClick={run}>
              <Icon className="ph-sym-action" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
        <div className="ios-group ph-card-fields">
          {fields.map((f) =>
            f.href || f.call ? (
              <button
                type="button"
                key={f.label}
                className="ios-row ph-field"
                onClick={f.href ? () => openLink(f.href) : noCalls}
              >
                <small>{f.label}</small>
                <span className="ph-field-link">{f.value}</span>
              </button>
            ) : (
              <div key={f.label} className="ios-row ph-field">
                <small>{f.label}</small>
                <span>{f.value}</span>
              </div>
            )
          )}
        </div>
      </div>
    </Screen>
  );
};

// Phone (iOS 26), like Sylvester's: Favourites, Recents, Contacts, Keypad and Voicemail under a
// floating tab bar; the (i) on a call, or a contact, opens their card. It opens on Recents.
const PhoneApp = ({ now, onClose }) => {
  const [tab, setTab] = useState("recents");
  const nav = useNavStack([{ screen: "tab" }]);
  const open = (person) => nav.push({ screen: "card", person });

  // Choosing a tab (even the current one) goes back to its first screen, as on iOS.
  const switchTab = (id) => {
    setTab(id);
    nav.reset([{ screen: "tab" }]);
  };

  const renderTab = () => {
    if (tab === "favourites") return <FavouritesScreen open={open} />;
    if (tab === "contacts") return <ContactsScreen open={open} />;
    if (tab === "keypad") return <KeypadScreen />;
    if (tab === "voicemail") return <VoicemailScreen />;
    return <RecentsScreen now={now} open={open} />;
  };

  return (
    <AppShell label="Phone" origin="16% 92%" onClose={onClose}>
      {nav.pages.map((entry) => (
        <div key={entry.key} {...nav.pageProps(entry, "ios-page--grouped ph-page")}>
          {entry.screen === "card" ? <CardScreen person={entry.person} back={nav.back} /> : renderTab()}
        </div>
      ))}
      <nav className="ph-tabs glass glass--light" aria-label="Phone">
        {TABS.map(([id, label, Icon]) => (
          <button
            type="button"
            key={id}
            className={`ph-tab ${tab === id ? "is-current" : ""}`}
            onClick={() => switchTab(id)}
            aria-current={tab === id ? "page" : undefined}
          >
            <Icon className={`ph-tab-icon ph-tab-icon--${id}`} aria-hidden="true" />
            {label}
          </button>
        ))}
      </nav>
    </AppShell>
  );
};

export default PhoneApp;
